import { FontWeight } from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import { StageLayer } from "@nolimitcity/slot-launcher/bin/StageLayer";
import { TextStyleOptions } from "../setting/text/IText";
import { ResizeHelper } from "../utils/ResizeHelper";
import { Helper } from "../utils/Helper";
import { MathHelper } from "../utils/MathHelper";
import { ArrayHelper } from "../utils/ArrayHelper";
import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { KeypadPluginEvents } from "@nolimitcity/slot-launcher/bin/plugins/KeypadPlugin";
import {  gsap } from "gsap";

export interface ISymbolValues {
    [symbolName: string]: {
        [numSymbols: number]: number;
    };
}

interface PayTableData{
    dispObj:PIXI.DisplayObject;
    fallBackTexture?:PIXI.Texture;
    name?:string;
}

interface PayTableScaling{
    portrait:PIXI.Point;
    landscape:PIXI.Point;
}

export interface MiniPayTableConfig{
    style?:TextStyleOptions;
    orientation_scale?:PayTableScaling;
    showBetValue?:boolean;
    xtraArea?:number;
    bgAlpha?:number;
}

export class MiniPayTable extends PIXI.Container implements StageLayer {

    private copyiedTarget:PIXI.Sprite;
    private target:PIXI.DisplayObject | undefined;

    private bgGraphic:PIXI.Graphics;
    protected valueText:PIXI.Text;
    private tl:gsap.core.Timeline;

    private symbolValues:ISymbolValues;
    private xtraArea:number = 30;

    private currentBetLvl:number = 1;
    private showBetValue:boolean = false;

    private static _instance:MiniPayTable;

    public static get instance():MiniPayTable{
        return this._instance;
    }

    private _oriScale:PayTableScaling = {
        portrait: new PIXI.Point(1,1),
        landscape: new PIXI.Point(1,1)
    };

    private DEFAULT_TEXT_STYLE:TextStyleOptions = {
        dropShadow: true,
        dropShadowAngle: 8,
        dropShadowBlur: 10,
        dropShadowDistance: 2,
        fontFamily : "Open Sans",
        fontWeight : FontWeight.BOLD,
        fontSize: 28,
        align:"left",
        wordWrap: true,
        wordWrapWidth: 400,
        fill: [
            "white",
        ]
    };

    constructor(values:ISymbolValues,config?:MiniPayTableConfig){
        super();
        this.symbolValues = values;
        
        this.setConfig(config);
        this.init();
        this.addBaseEventListeners();
        this.name = "MiniPayTable";

        MiniPayTable._instance = this;
    }

    protected setConfig(config?:MiniPayTableConfig):void{
        if(config){
            if(config.orientation_scale) this._oriScale = config.orientation_scale;
            if(config.style) this.DEFAULT_TEXT_STYLE = config.style;
            if(config.xtraArea != undefined) this.xtraArea = config.xtraArea;
            this.showBetValue = !!config.showBetValue;
        }
    }

    private get oriScale():PIXI.Point{ return ResizeHelper.isLandscape ? this._oriScale.landscape : this._oriScale.portrait };

    protected addBaseEventListeners():void{

        SlotGame.keypadPlugin.events.on(KeypadPluginEvents.USER_BET_UPDATE, (data:{from:number,to:number}) => {
            this.currentBetLvl = data.to;
        });
        
        // For fetching the start value.
        SlotGame.keypadPlugin.events.once(KeypadPluginEvents.DISPLAY_BET_UPDATE, (value:string) => {
            this.currentBetLvl = parseFloat(value);
        });
    }


    public stop():void{
        this.target = undefined;
        this.tl = Helper.setTimeline(this.tl, new gsap.core.Timeline(), true)
    }

    protected init():void{
        this.bgGraphic = this.addChild(new PIXI.Graphics());

        this.createCopySprite();
        this.createText("x1", this.DEFAULT_TEXT_STYLE);
    }

    protected createCopySprite():void{
        this.copyiedTarget = this.addChild(new PIXI.Sprite());
        this.copyiedTarget.anchor.set(0.5,0.5);
    }

    protected createText(text:string, style:TextStyleOptions):void {
        this.valueText = this.addChild(new PIXI.Text("x"+text, style));
        this.valueText.alpha = 0;
    }

    public onClicked(data:PayTableData):void{

        if(this.defineObjectFromData(data)){

            this.onResize();

            this.animateClick();
        }else{
            this.tl = Helper.setTimeline(this.tl, new gsap.core.Timeline(), true)
        }
    }

    protected defineObjectFromData(data:PayTableData):boolean{
        this.target = data.dispObj;

        // Copy the targetTexture for display.
        const texture:PIXI.Texture = (<PIXI.Sprite>data.dispObj).texture;

        // Set texture to be displayed.
        this.copyiedTarget.texture = texture ? texture : data.fallBackTexture!;

        // Find the symboltype.
        const sName:string = data.name ? data.name : texture.textureCacheIds[0].split("/")[0];
        return this.setText(sName);
    }

    protected setText(symValueTarget:string ):boolean{
        this.copyiedTarget.alpha = !!this.symbolValues[symValueTarget] ?  1 : 0;

        if(!this.symbolValues[symValueTarget]) return !!this.symbolValues[symValueTarget];
        // Getting Connections.
        const maxConn:string[] = Object.keys(this.symbolValues[symValueTarget]).map((symbolName:string) =>{ return  symbolName;});
        // Getting Connection values.
        const maxConnValues:number[] = ArrayHelper.initArrayWithValues((maxConn.length), (i:number) => { return this.symbolValues[symValueTarget][parseInt(maxConn[i])] } );
        
        this.valueText.text = "";

        if(this.showBetValue)
            maxConn.forEach((element,index) => {
          
                this.valueText.text += element +  "|"+ SlotGame.apiPlugin.currency.format((maxConnValues[index]*this.currentBetLvl)*.05);
            
                if(index+1 != maxConn.length) this.valueText.text += "\n";

            });
        else
            maxConn.forEach((element,index) => {

                this.valueText.text += element +  "|x"+ (maxConnValues[index]*.05).toFixed(2);
                
                if(index+1 != maxConn.length) this.valueText.text += "\n";
            });

        return true;
    }

    protected animateClick():void{
        this.alpha = 1;
        this.tl.fromTo([this.bgGraphic,this.valueText],{alpha:0},{duration:0.35,alpha:1},0);
        this.tl.to(this, {duration:0.5, alpha:0}, 2.25);
        this.tl.play();
    }

    public onResize():void{
        if(this.target != undefined){
            this.tl = Helper.setTimeline(this.tl, new gsap.core.Timeline(), true)

            this.defineCopiedSprite();

            this.defineText();

            this.defineGraphic();
        }
    }

    protected defineText():void{

        this.valueText.style.fontSize =  this.oriScale.x * <number>this.DEFAULT_TEXT_STYLE.fontSize;
        this.valueText.updateTransform();

        this.valueText.position = this.copyiedTarget.position;

        let targetTxtX2:number = this.copyiedTarget.position.x + this.copyiedTarget.width/2;
        if(this.leftPos ) targetTxtX2 = this.copyiedTarget.position.x - (this.valueText.width +this.copyiedTarget.width/2);

        targetTxtX2 +=  (this.leftPos? -1:1 ) * this.valueText.style.fontSize/10;

        const targetTxtY2:number = this.copyiedTarget.position.y - this.valueText.height/2;
        this.valueText.position.set(targetTxtX2, targetTxtY2);
    }

    protected defineCopiedSprite():void{

        this.copyiedTarget.scale = this.oriScale;
        this.copyiedTarget.position = Helper.copyLocalPosition(this.copyiedTarget,this.target!);

        const pivotOffset:PIXI.Point = MathHelper.pointSub(new PIXI.Point(0.5,0.5), (<PIXI.Sprite>this.target).anchor ? (<PIXI.Sprite>this.target).anchor : new PIXI.Point(0.5,0.5));
        const pivotPosOffset:PIXI.Point = MathHelper.pointMul(pivotOffset, new PIXI.Point(this.copyiedTarget.width,this.copyiedTarget.height));
        
        this.copyiedTarget.position = MathHelper.pointAdd(<PIXI.Point>this.copyiedTarget.position ,pivotPosOffset);
    }

    private get OffsetHeight():number{
        return ((<PIXI.Sprite>this.target).height - this.copyiedTarget.height);
    }

    private get largestHeight():number{
        return (this.valueText.height > this.copyiedTarget.height ? this.valueText.height  : this.copyiedTarget.height);
    }

    private get boxHeight():number{
        return this.largestHeight + this.OffsetHeight + this.xtraArea;
    }

    private get boxWidht():number{
        return this.valueText.width + this.copyiedTarget.width + (this.xtraArea);
    }

    private get leftPos():boolean{
        return this.boxWidht + Math.round(this.copyiedTarget.position.x) > 720;
    }

    private get leftPosOffset():number{
        return (this.leftPos ? 1: -1) * (this.boxWidht/2 - this.copyiedTarget.width/2 - this.xtraArea/2);
    }

    protected defineGraphic():void{
        this.bgGraphic.position = this.copyiedTarget.position;

        this.bgGraphic.clear();
        this.bgGraphic.lineStyle(3, 0xFFFFFF, 1)
        this.bgGraphic.beginFill(0x000000, 0.90);

        this.bgGraphic.drawRoundedRect(
            -this.boxWidht/2,
            -this.boxHeight/2,
            this.boxWidht,
            this.boxHeight,5);
        this.bgGraphic.endFill();
        this.bgGraphic.position.x -= this.leftPosOffset;
    }
}